import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Text, Icon, useTheme } from '@Sage/hoi-poi-ui';
import SetupStatusIcon from '../components/SetupStatusIcon';

const CampaignSection = memo(
    ({
        isCompleted = false,
        title,
        subtitle = null,
        actionLabel,
        isExpanded = false,
        onToggle,
        hideStatusIcon = false,
        isDimmed = false,
        children = null,
    }) => {
        const theme = useTheme();
        const expandedContentInnerRef = useRef(null);
        const [expandedContentHeight, setExpandedContentHeight] = useState(0);

        useEffect(() => {
            const contentNode = expandedContentInnerRef.current;

            if (!contentNode) {
                return undefined;
            }

            const updateExpandedContentHeight = () => {
                setExpandedContentHeight(contentNode.scrollHeight);
            };

            updateExpandedContentHeight();

            if (typeof ResizeObserver === 'undefined') {
                return undefined;
            }

            const resizeObserver = new ResizeObserver(() => {
                updateExpandedContentHeight();
            });

            resizeObserver.observe(contentNode);

            return () => {
                resizeObserver.disconnect();
            };
        }, [children, isExpanded]);

        const renderSubtitle = () => {
            if (!subtitle) return null;

            if (typeof subtitle === 'string') {
                return (
                    <Text type="body" className="campaign-details__section-subtitle">
                        {subtitle}
                    </Text>
                );
            }

            return subtitle;
        };

        const sectionClassName = `campaign-details__section ${
            isDimmed ? 'campaign-details__section--dimmed' : ''
        } ${isExpanded ? 'campaign-details__section--expanded' : ''}`;
        const mainRowClassName = `campaign-details__section-main-row ${
            hideStatusIcon ? 'campaign-details__section-main-row--no-icon' : ''
        }`;
        const expandedContentClassName = `campaign-details__section-expanded-content ${
            isExpanded ? 'campaign-details__section-expanded-content--open' : ''
        }`;
        const expandedContentStyle = useMemo(
            () => ({
                maxHeight: isExpanded ? `${expandedContentHeight}px` : '0px',
            }),
            [expandedContentHeight, isExpanded],
        );

        return (
            <div className={sectionClassName}>
                <div className={mainRowClassName}>
                    {!hideStatusIcon && (
                        <div className="campaign-details__section-icon">
                            <SetupStatusIcon isCompleted={isCompleted} />
                        </div>
                    )}
                    <div className="campaign-details__section-content">
                        <Text type="h6" className="campaign-details__section-title">
                            {title}
                        </Text>
                        {renderSubtitle()}
                    </div>
                    <div className="campaign-details__section-action">
                        <>
                            {isExpanded ? (
                                <Icon
                                    color={theme.colors.grey[500]}
                                    name="close"
                                    onClick={onToggle}
                                />
                            ) : (
                                <Button
                                    type="secondary"
                                    size="small"
                                    onClick={onToggle}
                                    disabled={isDimmed}
                                >
                                    {actionLabel}
                                </Button>
                            )}
                        </>
                    </div>
                </div>
                <div className={expandedContentClassName} style={expandedContentStyle}>
                    <div
                        ref={expandedContentInnerRef}
                        className="campaign-details__section-expanded-content-inner"
                    >
                        {children}
                    </div>
                </div>
            </div>
        );
    },
);

CampaignSection.displayName = 'CampaignSection';

CampaignSection.propTypes = {
    isCompleted: PropTypes.bool,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.node,
    actionLabel: PropTypes.string.isRequired,
    isExpanded: PropTypes.bool,
    onToggle: PropTypes.func.isRequired,
    hideStatusIcon: PropTypes.bool,
    isDimmed: PropTypes.bool,
    children: PropTypes.node,
};

export default CampaignSection;
